import {
  DefaultNamedDateRange,
  DefaultValue,
  FilterDefaultAsNumber,
  FilterDefaultValue,
  FilterType as DashboardFilterType,
  FilterType1,
  FilterType2,
  FilterType3,
  Reports,
} from '../api/types/generated/dashboard_get.response';
import { AvailableValues } from '../api/types/generated/filterData_get.response';
import {
  ReportDataStyle,
  ReportDisplayStyle,
} from '../api/types/generated/reports_get.response';
import { HttpMethod } from '../api/types/types';
import { FormTokenNode } from './context';

// [block key, submenu key] currently open in the side menu; undefined means nothing open at that level.
export type OpenMenu = (string | undefined)[];

export type MenuLeaf = {
  label?: string;
  url?: string;
  formId?: string;
  appId?: string;
  app?: {
    link: string;
    params?: string;
  };
  type?: string;
  children?: MenuLeaf[];
  key?: string;
};

export type MenuBlock = {
  label?: string;
  key?: string;
  icon?: string;
  children?: MenuLeaf[];
};

export type GlobalLocationStateType = {
  // For master grid
  fromForms?: boolean | undefined;
  submitted?: boolean | undefined;

  //For slave grid
  subsetItemId?: string | undefined;
  subsetSubmitted?: boolean | undefined;
  subsetFromForms?: boolean | undefined;

  // Filter first flows
  omitFilterFirstFilter?: boolean | undefined;
  fromFilterFirstInit?: boolean | undefined;

  // Toggled only in scenario when reload on form and base dataset is filter first
  fetchDataOnFilterFirstDataset?: boolean | undefined;

  // External url flags
  forceFindInDataset?: boolean | undefined;
  forceNewData?: boolean | undefined;
  itemId?: string | undefined;
  // Set on the /item -> /detail redirect so the control that opens a form can
  // mark the token as external-origin (the list holds only the single record).
  enteredFromExternalUrl?: boolean | undefined;
  // One-shot filter snapshot restored when opening a navigation quick bookmark.
  quickBookmarkFilters?: Record<string, unknown> | undefined;
  // Form flows
  rootToken?: FormTokenNode;
  childToken?: FormTokenNode;
  // Origin page when a form is opened from a menu leaf; redirect target on close
  menuFromUrl?: string | undefined;
  redirectedFromForm?: boolean | undefined;
  isDatasetDefinitionReloadRequired?: boolean | undefined;
  // Id of a row inserted from a FullscreenReference; auto-selected on return
  referenceInsertedId?: string | undefined;

  // Data export
  exportSelection?: {
    selectedRows?: unknown[];
    selectedVirtualRows?: number;
    selectAllChecked?: boolean;
    // Array, not Set — history state has to stay serializable.
    deselectedRowIds?: string[];
  };
  openExport?: boolean;

  //Other
  triggerInitData?: boolean | undefined;
  fromPathname?: string | undefined;
  filterItemName?: string | undefined;
  matchMode?: MatchMode | undefined;
  type?: 'master' | 'slave' | undefined;
};

export type FormItemType =
  | 'string'
  | 'integer'
  | 'float'
  | 'boolean'
  | 'binary'
  | 'date'
  | 'time'
  | 'dateTime'
  | 'json'
  | 'intRange'
  | 'numRange'
  | 'dateRange'
  | 'tsRange';

export enum RangePreset {
  CURRENT_WEEK = 'currentWeek',
  PREVIOUS_WEEK = 'previousWeek',
  NEXT_WEEK = 'nextWeek',
  CURRENT_MONTH = 'currentMonth',
  PREVIOUS_MONTH = 'previousMonth',
  CURRENT_YEAR = 'currentYear',
  PREVIOUS_YEAR = 'previousYear',
  LAST_7_DAYS = 'last7Days',
  LAST_2_MONTHS = 'last2Months',
  LAST_31_DAYS = 'last31Days',
  TO_NEXT_MONTH_END = 'toNextMonthEnd',
  TO_YEAR_END = 'toYearEnd',
  RECENT_21_DAYS = 'recent21Days',
  NEXT_14_DAYS = 'next14Days',
}

export enum DayPreset {
  CURRENT_DAY = 'currentDay',
  PREVIOUS_DAY = 'previousDay',
  TODAY = 'today',
  TOMORROW = 'tomorrow',
}

export interface FilterByDate {
  id: string;
  label: string;
  description?: string;
  annotation?: string;
  mandatory?: boolean;
  type: DashboardFilterType;
  minValue: string;
  maxValue: string;
  defaultValue?: DefaultValue;
  defaultNamedRange?: DefaultNamedDateRange;
}
export interface FilterByNumber {
  id: string;
  label: string;
  description?: string;
  annotation?: string;
  mandatory?: boolean;
  type: FilterType1;
  minValue: number;
  maxValue: number;
  defaultValue?: FilterDefaultAsNumber;
}
export interface FilterByListOfAvailableValues {
  id: string;
  label: string;
  description?: string;
  annotation?: string;
  mandatory?: boolean;
  type: FilterType2;
  defaultValue?: FilterDefaultValue;
  availableValues: AvailableValues;
}
export interface FilterByString {
  id: string;
  label: string;
  description?: string;
  annotation?: string;
  mandatory?: boolean;
  type: FilterType3;
  defaultValue?: string;
}

export type Filter =
  | FilterByDate
  | FilterByNumber
  | FilterByListOfAvailableValues
  | FilterByString;

export interface Dashboard {
  id: string;
  label: string;
  description?: string;
  annotation?: string;
  icon?: string;
  filters?: Filter[];
  reports?: Reports;
}

export type Report = {
  id: string;
  label: string;
  description?: string;
  annotation?: string;
  displayStyle?: ReportDisplayStyle;
  dataStyle?: ReportDataStyle;
  filters?: Filter[];
};

export type ColumnsWitdhType = Record<string, number> | undefined;

export type FormType = 'insert' | 'edit' | 'copy' | 'other';

export type DatasetFormDisplayType = {
  formId: string | null;
  formType: FormType;
  controlLabel: string | null;
};

export type GridInputValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | object;

export type ControlItemValue = string | number | boolean;

export type StaticControlItem = {
  label?: string;
  value: ControlItemValue;
};

// Options come from a property of the form GET/DEFAULT response holding a JSON array
export type ListItemControlItems = {
  listItem: string;
  valueItem?: string;
  labelItem?: string;
};

export type ControlItems =
  | StaticControlItem[]
  | ListItemControlItems
  | {
      apiMethod: ApiMethod;
      valueItem?: string;
      labelItem?: string;
    };

export type ReferenceDatasetControl = {
  name: string;
  label: string;
};

export interface ComboboxControl {
  type: 'combobox';
  items: ControlItems;
  default?: ControlItemValue;
}

export interface RadioButtonControl {
  type: 'radioBtn';
  items: ControlItems;
  default?: ControlItemValue;
}

export interface MultiCheckboxControl {
  type: 'multiCheckbox';
  items: ControlItems;
}

// Base64-encoded bitmap (e.g. a layout backgroundImage); item type 'binary'.
export type PictureControl = {
  type: 'picture';
  decorator?: DecoratorType;
};

// Color picker over a named palette: palette items are 6-char hex
// colors without the leading # ("pallet" spelling comes from the API schema).
export type ColorControl = {
  type: 'color';
  palletName?: string;
  palette?: string[];
  decorator?: DecoratorType;
};

type ControlRegExp = {
  exp: string;
  msg: string;
}[];

export type DecoratorType = string | number | boolean | null;

export interface ReferenceControl {
  type: 'reference';
  multiSelect?: boolean;
  datasetId: string;
  idItem: string;
  labelItem?: string;
  //TODO: Create proper filter type
  filter?: any;
  datasetControls?: ReferenceDatasetControl[];
  decorator?: DecoratorType;
}

export interface StringInputControl {
  type: 'string';
  minLen?: number;
  maxLen?: number;
  password?: boolean;
  regExp?: ControlRegExp;
  decorator?: DecoratorType;
}

export type TextAreaRendering = 'plain' | 'json';
export type TextAreaInputValue = string | null | object;

export interface TextAreaControl {
  type: 'text';
  minLen?: number;
  maxLen?: number;
  height?: number;
  regExp?: ControlRegExp;
  rendering?: TextAreaRendering;
}

export interface CheckboxSelectControl {
  type: 'checkbox';
  inverse?: boolean;
}

export interface IntegerInputControl {
  type: 'integer';
  min?: number;
  max?: number;
  decorator?: DecoratorType;
}

export interface RealInputControl {
  type: 'number';
  min?: number;
  max?: number;
  decorator?: DecoratorType;
}

export interface DateInputControl {
  type: 'date';
  min?: string;
  max?: string;
}

export interface TimeInputControl {
  type: 'time';
  min?: string;
  max?: string;
}

export interface DateTimeInputControl {
  type: 'dateTime';
  min?: string;
  max?: string;
  display?: 'simple' | 'combined';
}

export type StaticTextRendering = 'plain' | 'markdown' | 'json';
export type StaticTextInputValue = string | null;

export interface StaticTextControl {
  type: 'staticText';
  height?: number;
  decorator?: DecoratorType;
  rendering?: StaticTextRendering;
}

type LinkTarget = '_blank' | '_self' | '_parent';

export interface NavigationLink {
  type: 'link';
  target?: LinkTarget;
  hint?: string;
  template: string;
}

export interface NavigationDataset {
  type: 'dataset';
  datasetId: string;
}

export interface NavigationForm {
  type: 'form';
  formId: string;
  target?: LinkTarget;
  hint?: string;
}

export interface StaticStringControl {
  type: 'staticString';
  action?: NavigationLink | NavigationDataset | NavigationForm;
  decorator?: DecoratorType;
}

export interface StaticNumberControl {
  type: 'staticNumber';
  format?: string;
}

export interface LiteralTextControl {
  type: 'literalText';
  text?: string;
  style?: string;
}

export type Control =
  | ReferenceControl
  | ComboboxControl
  | RadioButtonControl
  | MultiCheckboxControl
  | PictureControl
  | ColorControl
  | StringInputControl
  | TextAreaControl
  | CheckboxSelectControl
  | IntegerInputControl
  | RealInputControl
  | DateInputControl
  | TimeInputControl
  | DateTimeInputControl
  | StaticTextControl
  | StaticStringControl
  | StaticNumberControl
  | LiteralTextControl;

export type ControlSelectionMode =
  | 'justone'
  | 'oneormore'
  | 'any'
  | 'dontcare'
  | 'dontcare_useall';

export type MatchMode =
  | 'endsWith'
  | 'startsWith'
  | 'custom'
  | 'contains'
  | 'in'
  | 'equals'
  | 'notEquals'
  | 'notContains'
  | 'notIn'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'between'
  | 'dateIs'
  | 'dateIsNot'
  | 'dateBetween'
  | 'dateTo'
  | 'dateFrom'
  | 'like'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'isNull'
  | 'isNotNull'
  | 'datetime-equals'
  | 'datetime-between'
  | 'datetime-notEquals';

export type FilterType =
  | 'string'
  | 'integer'
  | 'float'
  | 'boolean'
  | 'date'
  | 'time'
  | 'dateTime'
  | 'json'
  | 'intRange'
  | 'numRange'
  | 'dateRange'
  | 'timeRange'
  | 'tsRange'
  | 'unknown';

type FilterValue = string | number | boolean;
export type FilterError = string[] | null;

export interface FilterItemType {
  name: string;
  label: string;
  hint?: string;
  allowVisible?: boolean;
  type?: FilterType;
  control?: Control;
  default?: {
    matchMode?: MatchMode;
    value?: FilterValue;
  };
}

export interface SelectedFilter {
  name: string;
  datasetId: string;
  label: string;
  hint?: string;
  type: FilterType;
  control: Control;
  matchMode: MatchMode;
  value: FilterValue;
  error: FilterError;
}

export type ReferenceValue = string | number | null;

// Form multi-select reference values are `;`-delimited; filters and single-select stay `,`.
export type ReferenceValueDelimiter = ';' | ',';

export type ReferenceItem = {
  value: ReferenceValue;
  label: string;
};

export type SetDatasetFormDisplayFn = (value: DatasetFormDisplayType) => void;

export type FullscreenReferenceType = {
  name: string;
  datasetId: string;
  control: ReferenceControl;
  value: string;
  label: string;
  isFormInput?: boolean;
};

export type ForceErrorAnswer = {
  label: string;
  value: string;
};

export type EntryErrorValueType =
  | 'string'
  | 'int'
  | 'float'
  | 'date'
  | 'time'
  | 'datetime'
  | 'list'
  | 'card';

export type EntryErrorFieldType =
  | 'text'
  | 'int'
  | 'float'
  | 'boolean'
  | 'date'
  | 'time'
  | 'datetime';

export type EntryErrorListField = {
  name: string;
  // Only used when list data comes from the inlined 'data' property.
  type?: EntryErrorFieldType;
  // Value of this field is used as the return value. Multiple return fields
  // are concatenated with '|'.
  return?: boolean;
  // Column caption. Fields without a label are not displayed.
  label?: string;
  hint?: string;
  alignment?: 'left' | 'right' | 'center';
  // Only 'px' widths are supported.
  width?: string;
  formatStr?: string;
  // Format string for this field is taken from another field of the row.
  formatField?: string;
};

export type EntryErrorList = {
  // SQL query list variant — the query runs on the server; not supported by
  // this client (no endpoint to execute it), only the 'data' variant is.
  sql?: string;
  // Inlined data variant — array of rows, row fields follow 'fields' order.
  data?: (string | number | null)[][];
  fields: EntryErrorListField[];
};

// Parsed |E{...} definition received in error.entry of an ASW error response.
export type EntryErrorDefinition = {
  // Force value identifier — sent back as force=value in a_FORCE.
  force: string;
  type: EntryErrorValueType;
  default?: string | number;
  list?: EntryErrorList;
  // Card reader purpose identifier(s), e.g. CARDZAKAZNIK. Card readers are
  // not supported by this client; manual entry is offered when allowed.
  readers?: string;
  allowManual?: boolean;
};

export type BreadCrumbCrumb = {
  url: string;
  name: string;
  tokenId: string;
  isActive: boolean;
};

type JSONPrimitive = string | number | boolean | null;
export type JSONValue = JSONPrimitive | JSONObject | JSONArray;
export type JSONArray = Array<JSONValue>;
export type JSONObject = { [key: string]: JSONValue };

export type GridRow = Record<string, string | number | boolean | null>;
export type SimpleApiMethod = string;

export type DetailedApiMethod = {
  path: string;
  httpMethod?: HttpMethod; // default GET
  headers?: { [key: string]: string | number | boolean | null };
  queryParams?: { [key: string]: string | string[] | JSONObject };
  content?: string | JSONObject;
  // JSONata template applied to the result of the request. Evaluated against
  // the same data as content and queryParams, with the received body added as
  // "response". Form handlers use it to remap onto form field names.
  response?: JSONObject | string;
  // Like response, but applied to the body when the HTTP code is not 2xx,
  // before error processing. Same evaluation data as response — the error body
  // arrives under "response".
  errorResponse?: JSONObject | string;
  bulk?: any;
  responseActions?: {
    // In forms only
    storeAlways?: boolean | string | null;
    // In forms only
    setFocus?: string | null;
    // In dataset only
    reload?: string | boolean | null;
    // In dataset only
    removeRows?: string | string[];
  };
};

export type ApiMethod = SimpleApiMethod | DetailedApiMethod;

export type MultiSelectMode = 'sequential' | 'bulk';

export type FormControlAction = {
  type: 'form';
  formType: FormType;
  formId: string;
  multiSelectMode?: MultiSelectMode;
};

export type PrintControlAction = {
  type: 'print';
  printId: string;
};

export type ExportControlAction = {
  type: 'export';
};

export type MethodControlAction = {
  type: 'method';
  apiMethod: ApiMethod;
  params?: { key: string; value: string | number | boolean }[];
};

export type AggregateControlAction = {
  type: 'aggregate';
  apiMethod: ApiMethod;
  items: {
    item?: string;
    label: string;
    operation: 'count' | 'min' | 'max' | 'sum' | 'avg';
    grouping?: {
      item: string;
      labelItem?: string;
      formatItem?: string;
    };
    formatItem?: string;
    format?: string;
  }[];
};

export type DatasetControlAction = {
  type: 'dataset';
  datasetId: string;
  navigateToSlave?: boolean;
  data?: Record<string, unknown>;
};

export type ApplicationControlAction = {
  type: 'app';
  link: string;
  params?: string;
};

export type LayoutsControlAction = {
  type: 'layouts';
  layoutId: string;
  objectId?: string;
};

export type DatasetCondition = {
  source?: 'currentRow' | 'masterRow';
  field: string;
  equals?: unknown;
  notEquals?: unknown;
  in?: unknown[];
};

export type DatasetControl = {
  name?: string;
  icon?: string;
  customIcon?: string;
  allowVisible?: boolean;
  label: string;
  hint?: string;
  visibleWhen?: DatasetCondition;
  selectionMode?: ControlSelectionMode;
  confirmMsg0?: string;
  confirmMsg1?: string;
  confirmMsgX?: string;
  confirmMsgXMore?: string;
  confirmMsgXDelimiter?: 'comma' | 'semicolon' | 'newline';
  action:
    | FormControlAction
    | PrintControlAction
    | ExportControlAction
    | MethodControlAction
    | AggregateControlAction
    | DatasetControlAction
    | ApplicationControlAction
    | LayoutsControlAction;
};

export type DatasetItem = {
  name: string;
  label?: string;
  hint?: string;
  visible?: boolean | string;
  detailVisible?: boolean | string;
  allowVisible?: boolean;
  uiConfigAllowVisible?: JSONObject;
  type?: string;
  format?: string;
  formatItem?: string;
  /** Stable stored value to the already localized display label. */
  valueLabels?: Record<string, string>;
  unknownValueLabel?: string;
  alignment?: 'left' | 'center' | 'right';
  minWidth?: number;
  maxWidth?: number;
  /** Default print footer aggregation. False omits the column from the footer. */
  reportAggregate?: 'sum' | 'avg' | 'min' | 'max' | false;
  /** Overrides grid visibility for the generated system print template. */
  printVisible?: boolean;
  filterParam?: string;
  rendering?: string;
  inlineEdit?: {
    apiMethod: string;
    min?: number;
    max?: number;
    enabledWhen?: DatasetCondition;
  };
  [key: string]: any;
};

export type Dataset = {
  apiMethod: ApiMethod;
  label: string;
  controls?: DatasetControl[];
  items?: DatasetItem[];
  labelItem?: string;
  styleItem?: string;
  additionalItems?: string[];
  sort?: { item: string; dir?: 'asc' | 'desc' }[];
  useDetail?: boolean;
  /** Optional rowType allow-list used only by the on-screen grid. */
  gridRowTypes?: string[];
  /** Optional item allow-list used only by the on-screen grid. */
  gridItemNames?: string[];
  /** Whether the generated system print footer includes the data-row count. */
  printRowCount?: boolean;
  /** Whether the dataset may be exported from the generic grid header. */
  exportable?: boolean;
  [key: string]: any;
};

export type FormControl = {
  type: 'call';
  label?: string;
  apiMethodOverride?: ApiMethod;
};

export type HandlerTriggerControl = {
  type: 'handlerTrigger';
  method: string;
  label: string;
  hint?: string;
  triggerData?: JSONObject;
};

export type HandlerAction =
  | 'change'
  | 'touch'
  | 'focusIn'
  | 'focusOut'
  | 'focusOutChanged';

export type FormItemHandlerTrigger = {
  // Exactly one of method/output is set. method calls a named apiMethod over
  // HTTP; output is resolved locally — string = JSONata expression, object =
  // JSON template with {{ }} placeholders.
  method?: string;
  output?: string | JSONObject;
  actions: HandlerAction[];
};

export type FormApiMethod = {
  default?: string | DetailedApiMethod;
  get?: string | DetailedApiMethod;
  insert?: string | DetailedApiMethod;
  update?: string | DetailedApiMethod;
  [key: string]: string | DetailedApiMethod | undefined;
};

export type FormItemSaved = {
  omitIfEmpty?: boolean;
  onPOST?: boolean;
  onPUT?: boolean;
};

export type FormItem = {
  name?: string;
  expression?: string;
  label: string;
  hint?: string;
  /** Legacy alias of `hint` used by some v2.0 form definitions. */
  help?: string;
  width?: number;
  type: FormItemType;
  mandatory?: boolean;
  visible?: boolean | string;
  allowVisible?: boolean;
  editable?: boolean | string;
  allowEditable?: boolean;
  group?: string;
  section?: string;
  default?: string | number | boolean;
  control?: Control;
  saved?: FormItemSaved;
  handlerTriggers?: FormItemHandlerTrigger[];
  [key: string]: any;
};

export type FormGroup = {
  name: string;
  caption: string;
  visible?: boolean | string;
  allowVisible?: boolean;
};

export type FormSection = {
  name: string;
  caption: string;
  visible?: boolean | string;
  allowVisible?: boolean;
};

export type Form = {
  version?: string;
  apiMethods: FormApiMethod;
  /** Optional translation key overriding the primary submit action caption. */
  submitLabel?: string;
  allowVisible?: boolean;
  allowEditable?: boolean;
  captions?: {
    insert?: string;
    edit?: string;
  };
  captionItem?: string;
  postMultiple?: boolean;
  controls?: (FormControl | HandlerTriggerControl)[];
  items: FormItem[];
  groups?: FormGroup[];
  sections?: FormSection[];
};

export type FieldHandlerCallbacks = {
  onChange?: (value: unknown) => void;
  onTouch?: (value?: unknown) => void;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
};

export type LayoutInlineForm = {
  items: FormItem[];
  groups?: FormGroup[];
  sections?: FormSection[];
  apiMethods?: FormApiMethod;
};

export type LayoutDefinition = {
  version?: string;
  caption?: string;
  // Arrangement mode is fixed per layout type — part of the definition, never
  // of the layout data (a concrete layout cannot switch it).
  mode?: LayoutCanvasMode;
  apiMethods: {
    get: ApiMethod;
    put: ApiMethod;
    matrixDefault?: ApiMethod;
    matrixGenerate?: ApiMethod;
  };
  objectForm: LayoutInlineForm;
  canvasForm?: LayoutInlineForm;
  matrixForm?: LayoutInlineForm;
  allowProjection?: boolean;
};

export type LayoutCanvasMode = 'free' | 'fixed';

export type LayoutCanvas = {
  width: number;
  height: number;
  backgroundColor: string;
  columns?: number;
  rows?: number;
  gridX?: number;
  gridY?: number;
  // Gap between grid cells (QA-268; formerly offsetX/offsetY).
  gapX?: number;
  gapY?: number;
  snapGrid?: boolean;
  showPoints?: boolean;
  showVLines?: boolean;
  showHLines?: boolean;
  lockItems?: boolean;
  bestOfScale?: boolean;
  scaleX?: number;
  scaleY?: number;
  backgroundImage?: string | null;
  // Fixed mode: default styling of the buttons (an object's own values win) —
  // fixed objects are data-only, so the styling lives on the canvas.
  objectBackgroundColor?: string;
  fontColor?: string;
  fontFamily?: string;
  fontSize?: number;
  // The API sends the canvas-level style as a string ("bold"), the object
  // form works with the array — normalizeLayoutFontStyle unifies.
  fontStyle?: LayoutFontStyle[] | string;
  // additionalProperties: true — custom editor preference keys round-trip through the API unchanged.
  [key: string]: unknown;
};

export type LayoutFontStyle = 'bold' | 'italic' | 'underline' | 'strikethrough';

export type LayoutObject = {
  // Server-assigned identity; absent on objects created in the editor before
  // the first save. Pairs the live object with its dirty/new baseline counterpart.
  id?: number;
  // FE-only identity assigned when the object enters the editor state.
  feKey?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  caption?: string;
  fontFamily?: string;
  fontSize?: number;
  fontStyle?: LayoutFontStyle[];
  fontColor?: string;
  backgroundColor?: string;
  backgroundImage?: string | null;
  // none | topLeft | center | centerFit — see getLayoutImagePlacement.
  backgroundImageMethod?: string | null;
  type?: string;
  [key: string]: unknown;
};

export type Layout = {
  name?: string;
  annotation?: string;
  comment?: string;
  // Fixed layouts have no canvas — the grid derives from the object order.
  canvas?: LayoutCanvas;
  objects: LayoutObject[];
};
